<template>
    <!-- Customer -->
    <td :class="props.tdClass">
      <div class="flex items-center">
        <!--<img src="https://ovatu.com/marketing/images/team/dave_transparent.png" alt="" class="flex-shrink-0 h-8 w-8 rounded-full">-->
        <o-avatar :items="[attendee.customer]" />
        <div class="ml-2 text-sm font-normal">
          <p class="leading-tight">
            {{ attendee.customer.firstName }} {{ attendee.customer.lastName }}
          </p>
          <p v-if="hasResources" class="block text-sm text-gray-600 dark:text-gray-300">
            <span v-for="customerResource in attendee.resources" :key="customerResource.idReservationCustomerResource">
              {{ resourceLabel(customerResource) }}
            </span>
          </p>
        </div>
      </div>
    </td>

    <!-- Status -->
    <td :class="props.tdClass">
      <div class="flex items-center flex-wrap" @click.prevent.stop>
        <o-chip v-if="isCourseRunCustomer" class="mr-1 mb-1" size="sm" flat variant="info">
          Course
        </o-chip>

        <o-chip v-if="isDeleted" class="mr-1 mb-1" size="sm" flat variant="danger">
          {{ $t('general.deleted') }}
        </o-chip>

        <o-chip v-if="isStatusCancelled" class="mr-1 mb-1" size="sm" flat variant="danger">
          {{ $t('general.cancelled') }}
        </o-chip>

        <o-chip v-if="isStatusConfirmed" class="mr-1 mb-1" size="sm" flat variant="success">
          {{ $t('reservation.confirmed') }}
        </o-chip>

        <o-chip v-if="isStatusUnconfirmed" class="mr-1 mb-1" size="sm" flat variant="warning">
          {{ $t('reservation.unconfirmed') }}
        </o-chip>

        <o-chip
          v-if="hasPass"
          size="sm"
          flat
          class="mr-1 mb-1"
          variant="success"
        >
          Pass
        </o-chip>

        <o-chip
          v-if="isCheckedIn"
          size="sm"
          flat
          class="mr-1 mb-1"
          variant="info"
        >
          {{ $t('reservation.checkedIn') }}
        </o-chip>

        <o-chip
          v-if="isNoShow"
          size="sm"
          flat
          class="mr-1 mb-1"
          variant="warning"
        >
          {{ $t('reservation.noShow') }}
        </o-chip>

        <o-chip v-if="isCustomerConfirmed" class="mr-1 mb-1" size="sm" flat variant="success">
          {{ $t('reservation.attending') }}
        </o-chip>

        <o-chip v-if="isPaymentDetailsCaptured" class="mr-1 mb-1" size="sm" flat variant="success">
          {{ $t('reservation.paymentDetailsCaptured') }}
        </o-chip>

        <o-chip v-if="onlineTermsAgreed" class="mr-1 mb-1" size="sm" flat variant="success">
          {{ $t('reservation.termsAgreed') }}
        </o-chip>

        <o-chip v-if="attendee.makeupDetails?.isMakeup" class="mr-1 mb-1" size="sm" flat variant="info">
          {{ $t('components.reservation.edit.attendee.makeup') }}
        </o-chip>

        <o-panel-link
          v-if="attendee.makeupDetails?.makeupScheduled"
          :path="`/appointment/attendee/${attendee.makeupDetails.scheduledReservationCustomer?.idReservationCustomer}`"
          @click.stop
        >
          <o-chip class="mr-1 mb-1" size="sm" flat variant="info">
            {{ $t('components.reservation.edit.attendee.makeupOn', { date: $date.convert(attendee.makeupDetails.scheduledReservationCustomer?.reservation?.date, undefined, $date.presets.short) }) }}
          </o-chip>
        </o-panel-link>

        <o-button
          v-else-if="attendee.makeupDetails?.makeupAvailable"
          size="sm"
          class="mr-1 mb-1"
          @click="handleBookMakeup"
        >
          {{ $t('components.reservation.edit.attendee.scheduleMakeup') }}
        </o-button>
      </div>
    </td>

    <!-- Price -->
    <td :class="props.tdClass">
      <div v-if="isCourseRunCustomer" class="leading-tight">
        <p class="font-medium text-sm text-green-600">
          Included in course
        </p>
      </div>
      <div v-if="!isCourseRunCustomer" class="leading-tight">
        <div v-if="hasDiscount" class="pb-1 mb-1 border-b text-xs">
          <p>{{ attendee.priceLabel }}</p>
          <p>{{ $t('components.reservation.edit.attendee.discount', { label: attendee.discountLabel }) }}</p>
        </div>
        <p class="font-semibold text-sm" :class="[ isPaid ? 'text-green-600' : 'text-red-600' ]">
          {{ attendee.discountedPriceLabel }}
        </p>
      </div>
    </td>

    <!-- Notes -->
    <td :class="props.tdClass">
      <p>{{ attendee.notes }}</p>
    </td>

    <!-- Actions -->
    <td :class="props.tdClass">
      <div class="flex items-center justify-end space-x-1">
        <o-tooltip v-if="isStatusUnconfirmed" :text="$t('reservation.confirmCustomer')">
          <o-button
            tag="o-button"
            size="sm"
            icon="reservationConfirm"
            flat
            @click="handleConfirm"
          />
        </o-tooltip>

        <o-tooltip v-if="!attendee.checkedIn" :text="$t('reservation.checkInCustomer')">
          <o-button
            tag="o-button"
            size="sm"
            icon="success"
            flat
            @click="handleCheckIn"
          />
        </o-tooltip>

        <o-tooltip v-if="!attendee.checkedIn" :text="$t('components.reservation.edit.attendee.noShow')">
          <o-button
            tag="o-button"
            size="sm"
            icon="x"
            flat
            @click="handleNoShow"
          />
        </o-tooltip>

        <o-tooltip v-if="!isCourseRunCustomer && attendee.order && $grants.all({ order: ['view'] })" :text="$t('order.view')">
          <o-panel-link
            size="sm"
            icon="sale"
            flat
            tag="o-button"
            :path="`/order/${attendee.order.idOrder}`"
          />
        </o-tooltip>

        <o-tooltip v-if="!isCourseRunCustomer && !attendee.customerPass && !attendee.order && $grants.all({ order: ['create'] })" :text="$t('order.new')">
          <o-button
            size="sm"
            icon="sale"
            flat
            variant="primary"
            @click="handleCreateOrder"
          />
        </o-tooltip>

        <o-tooltip v-if="isCourseRunCustomer" :text="$t('components.reservation.edit.attendee.viewCourseEnrolment')">
          <o-panel-link
            tag="o-button"
            size="sm"
            :path="`/customer/course/${attendee.customerCourse.idCustomerCourse}`"
            icon="course"
            flat
          />
        </o-tooltip>

        <o-tooltip text="Edit class customer">
          <o-panel-link
            tag="o-button"
            size="sm"
            :path="`/appointment/attendee/${attendee.idReservationCustomer}`"
            icon="edit"
            flat
          />
        </o-tooltip>

        <o-tooltip v-if="!isStatusCancelled" :text="$t('components.reservation.edit.attendee.remove')">
          <o-button
            variant="danger"
            size="sm"
            flat
            icon="reservationCancel"
            @click="handleCancel"
          />
        </o-tooltip>

        <cancel-dialog
          ref="cancelDialog"
          :portal="panel.portal"
          :reservation="attendee"
          :cancellation-types="reservation.options.cancellationTypes || []"
          @update="$emit('update')"
          @update:loading="$emit('update:loading', $event)"
        />
      </div>

      <check-in-dialog
        ref="checkInDialog"
        :reservation="attendee"
        :service="reservation.service"
        :employee="reservation.employee"
        @update="$emit('update')"
        @update:loading="$emit('update:loading', $event)"
      />

      <no-show-dialog
        ref="noShowDialog"
        :reservation="attendee"
        @update="$emit('update')"
        @update:loading="$emit('update:loading', $event)"
      />

      <confirm-dialog
        ref="confirmDialog"
        :reservation="attendee"
        @update="$emit('update')"
        @update:loading="$emit('update:loading', $event)"
      />

      <book-makeup-dialog
        v-if="attendee.customerCourse"
        ref="bookMakeupDialog"
        :portal="panel.portal"
        @update:loading="$emit('update:loading', $event)"
        @success="$emit('update')"
      />
    </td>
</template>

<script setup lang="ts">
import CancelDialog from '@/components/reservation/actions/Cancel.vue'
import CheckInDialog from '@/components/reservationCustomer/actions/CheckIn.vue'
import NoShowDialog from '@/components/reservation/actions/NoShow.vue'
import ConfirmDialog from '@/components/reservationCustomer/actions/Confirm.vue'
import BookMakeupDialog from '@/components/customerCourse/actions/BookMakeup.vue'

import {
  OAvatar,
} from '@/components/elements'

const panel = inject<any>('panel')
const { $i18n } = useNuxtApp()

interface Props {
  attendee: Record<string, any>
  reservation: Record<string, any>
  props: Record<string, any>
}

interface Emits {
  (e: 'update', value?: any): void
  (e: 'update:loading', value: boolean): void
}

const componentProps = defineProps<Props>()
const emit = defineEmits<Emits>()

const cancelDialog = ref<any>()
const checkInDialog = ref<any>()
const noShowDialog = ref<any>()
const confirmDialog = ref<any>()
const bookMakeupDialog = ref<any>()

const { createOrder } = useOrderCreate()

const isCheckedIn = computed(() => componentProps.attendee.checkedIn)
const isNoShow = computed(() => componentProps.attendee.noShow)
const isDeleted = computed(() => componentProps.attendee.deleted)
const isStatusCancelled = computed(() => componentProps.attendee.isStatusCancelled)
const isStatusConfirmed = computed(() => componentProps.attendee.isStatusConfirmed)
const isStatusUnconfirmed = computed(() => componentProps.attendee.isStatusUnconfirmed)
const isPaid = computed(() => componentProps.attendee.isPaid)
const isCustomerConfirmed = computed(() => componentProps.attendee.customerConfirmed)
const onlineTermsAgreed = computed(() => componentProps.attendee.onlineTermsAgreed)
const isPaymentDetailsCaptured = computed(() => componentProps.attendee.paymentDetailsCaptured)
const isCourseRunCustomer = computed(() => !!componentProps.attendee.customerCourse)
const hasPass = computed(() => !!componentProps.attendee.customerPass)
const hasResources = computed(() => !!componentProps.attendee.resources.length)
const hasDiscount = computed(() => !!componentProps.attendee.discountTotal)
const hasNotes = computed(() => !!componentProps.attendee.notes)

function resourceLabel (customerResource: any) {
  const serviceResourceName = customerResource.serviceResource?.name
  const resourceName = customerResource.resource?.displayName

  if (!resourceName) {
    return serviceResourceName
  }

  if (!serviceResourceName) {
    return resourceName
  }

  return $i18n.t('general.colonSeparator', { label1: serviceResourceName, label2: resourceName })
}

function handleCancel () {
  cancelDialog.value.open()
}

function handleCheckIn () {
  checkInDialog.value.open()
}

function handleNoShow () {
  noShowDialog.value.open()
}

function handleConfirm () {
  confirmDialog.value.open()
}

async function handleCreateOrder () {
  emit('update:loading', true)

  const customer = componentProps.attendee.customer
  const items = [componentProps.attendee]

  await createOrder({ items, customer })

  emit('update:loading', false)
}

function handleBookMakeup () {
  bookMakeupDialog.value.open(
    componentProps.attendee.customerCourse,
    componentProps.attendee.idReservationCustomer,
  )
}
</script>
